<template>
	<section
		id="faq"
		class="my-16 md:my-0 md:mb-32"
		itemscope
		itemtype="https://schema.org/FAQPage"
	>
		<!-- FAQ Desktop layout -->
		<div class="hidden md:block">
			<div class="mx-auto px-2 sm:px-6 lg:px-4 max-w-sm md:max-w-6xl">
				<div class="flex flex-col items-center gap-3 mb-12">
					<p class="body-2">FAQ</p>
					<h3 class="heading-2">Frequently Asked Questions</h3>
					<p class="sub-heading-1 max-w-2xl text-center">
						Find quick answers to common questions about our tours, booking
						process, and services to help you plan your trip with ease.
					</p>
				</div>

				<div class="grid grid-cols-3 gap-8">
					<!-- Category Navigation -->
					<div class="col-span-1 space-y-3">
						<div
							v-for="(category, categoryIndex) in faqCategories"
							:key="categoryIndex"
							class="space-y-2"
						>
							<button
								type="button"
								@click="selectCategory(categoryIndex)"
								:class="[
									'w-full px-6 py-4 rounded-lg flex items-center justify-center transition-all duration-200 font-medium body-1',
									selectedCategory === categoryIndex
										? 'bg-black text-white shadow-lg'
										: 'bg-white shadow border border-gray-200 text-black hover:bg-gray-50',
								]"
							>
								{{ category.name }}
							</button>
						</div>
					</div>

					<!-- Questions and Answers -->
					<div class="col-span-2 space-y-3">
						<div
							v-for="(item, itemIndex) in getSelectedCategoryQuestions()"
							:key="itemIndex"
							class="border border-gray-200 rounded-lg overflow-hidden"
							itemscope
							itemprop="mainEntity"
							itemtype="https://schema.org/Question"
						>
							<button
								type="button"
								@click="toggleDesktopQuestion(itemIndex)"
								class="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
								:aria-expanded="desktopQuestionStates[itemIndex]"
								:aria-controls="`faq-desktop-answer-${itemIndex}`"
							>
								<h4 class="font-medium body-1" itemprop="name">
									{{ item.question }}
								</h4>
								<svg
									:class="desktopQuestionStates[itemIndex] ? 'rotate-180' : ''"
									class="w-6 h-6 transition-transform duration-200 shrink-0 ml-2"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
									aria-hidden="true"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M19 9l-7 7-7-7"
									></path>
								</svg>
							</button>
							<!-- Use grid 0fr/1fr instead of v-show so answers are never display:none (SEO / FAQ rich results) -->
							<div
								class="grid overflow-hidden transition-[grid-template-rows] duration-200 ease-out border-t border-gray-100"
								:class="
									desktopQuestionStates[itemIndex]
										? 'grid-rows-[1fr]'
										: 'grid-rows-[0fr]'
								"
							>
								<div class="min-h-0">
									<div
										:id="`faq-desktop-answer-${itemIndex}`"
										class="px-6 pb-4 text-gray-600"
										role="region"
										:aria-hidden="!desktopQuestionStates[itemIndex]"
										itemscope
										itemprop="acceptedAnswer"
										itemtype="https://schema.org/Answer"
									>
										<p
											class="pt-4 leading-relaxed body-1 whitespace-pre-line"
											itemprop="text"
										>
											{{ item.answer }}
										</p>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- FAQ mobile layout -->
		<div class="md:hidden">
			<Container>
				<div class="mx-auto px-0 sm:px-6 lg:px-4 max-w-sm md:max-w-6xl">
					<div class="flex flex-col items-center text-center gap-2 mb-7">
						<p class="body-2">FAQ</p>
						<p class="heading-2">Frequently Asked Questions</p>
						<p class="body-1">
							Find quick answers to common questions about our tours, booking
							process, and services to help you plan your trip with ease.
						</p>
					</div>

					<div class="space-y-2">
						<div
							v-for="(category, categoryIndex) in faqCategories"
							:key="categoryIndex"
							class="border border-gray-200 rounded-lg overflow-hidden"
						>
							<button
								type="button"
								@click="toggleCategory(categoryIndex)"
								class="w-full px-5 py-5 text-left flex items-center justify-between bg-black text-white"
								:aria-expanded="categoryStates[categoryIndex]"
							>
								<span class="body-2">{{ category.name }}</span>
								<svg
									:class="categoryStates[categoryIndex] ? 'rotate-180' : ''"
									class="w-5 h-5 transition-transform duration-200 shrink-0"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
									aria-hidden="true"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M19 9l-7 7-7-7"
									></path>
								</svg>
							</button>

							<div
								class="grid overflow-hidden transition-[grid-template-rows] duration-200 ease-out border-t border-gray-100"
								:class="
									categoryStates[categoryIndex]
										? 'grid-rows-[1fr]'
										: 'grid-rows-[0fr]'
								"
							>
								<div class="min-h-0">
									<div
										v-for="(item, itemIndex) in category.questions"
										:key="itemIndex"
										class="border-b border-gray-100 last:border-b-0"
									>
										<button
											type="button"
											@click="toggleQuestion(categoryIndex, itemIndex)"
											class="w-full px-6 py-3 text-left flex items-center justify-between hover:bg-gray-50 transition-colors bg-gray-50"
											:aria-expanded="
												Boolean(questionStates[categoryIndex]?.[itemIndex])
											"
											:aria-controls="`faq-mobile-answer-${categoryIndex}-${itemIndex}`"
										>
											<h4 class="body-2">{{ item.question }}</h4>
										</button>
										<div
											class="grid overflow-hidden transition-[grid-template-rows] duration-200 ease-out bg-white"
											:class="
												questionStates[categoryIndex]?.[itemIndex]
													? 'grid-rows-[1fr]'
													: 'grid-rows-[0fr]'
											"
										>
											<div class="min-h-0">
												<div
													:id="`faq-mobile-answer-${categoryIndex}-${itemIndex}`"
													class="px-6 pb-3 text-gray-600"
													role="region"
													:aria-hidden="
														!questionStates[categoryIndex]?.[itemIndex]
													"
												>
													<p class="pt-3 body-2 whitespace-pre-line">
														{{ item.answer }}
													</p>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</Container>
		</div>
	</section>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import Container from "./Container.vue";

interface FAQQuestion {
	question: string;
	answer: string;
}

interface FAQCategory {
	name: string;
	questions: FAQQuestion[];
}

// FAQ Data
const faqCategories = ref<FAQCategory[]>([
	{
		name: "Payment and Booking",
		questions: [
			{
				question: "How do I book an activity via website?",
				answer:
					"Booking Process:\n1. Go to the Activities section.\n2. Select the activity you would like to book.\n3. Choose the activity provided such as rafting, kayaking, ATV ride, cycling, and trekking (e.g., Sharing Rafting or Private Rafting).\n4. Select your preferred date and time.\n5. Enter the number of participants.\n6. If you would like to add more activities, select them from the Add-ons section and enter the quantity.\n7. If you do not need any additional activities, click Checkout.\n8. Complete the guest information form with:\n- Full Name\n- Email Address\n- Phone Number\n9. Select your preferred payment option and complete the payment.\n\nAfter Payment:\n• A booking confirmation email will be sent to your registered email address\n• The email will contain your booking details and confirmation information\n• During check-in process, you can show the confirmation email to our front office staff for the registration process",
			},
			{
				question: "How do I complete the payment?",
				answer:
					"We offer 2 payment options if you book through our website, which are full payment and 50% down payment. If you choose the 50% down payment, you can pay the remaining balance on-site in cash or by card. For your information, payment by card will incur an additional charge 3% for the balance payment",
			},
			{
				question: "Is it possible if I come directly without a reservation?",
				answer:
					"Yes, walk-in guests are welcome. However, please note that check-in must be completed within the designated time frame for your selected activity. We highly recommend making a reservation in advance to ensure availability of spots for your preferred activity, especially during the high season.",
			},
			{
				question: "What are the available payment methods?",
				answer:
					"We accept payments via bank transfer, credit/debit card, and e-wallet through our official payment gateway. Credit card payments are accepted by Visa and Mastercard. Further instructions will be provided after booking.",
			},
			{
				question: "What is the cancellation and rescheduling policy?",
				answer:
					"- Full Refund (100%): Cancellations made at least 1 day before the activity date (no later than 5:00 PM) are eligible for a full refund.\n- Partial Refund (50%): Cancellations made after 5:00 PM on the day before the activity, or anytime on the activity day before the activity starts, will receive a 50% refund, regardless of the reason.\n- No Show: Guests who do not show up without prior notice are not eligible for a refund.\n- Rescheduling is free of charge and subject to slot availability.",
			},
		],
	},
	{
		name: "Location and Transportation",
		questions: [
			{
				question: "Is transportation included in the package?",
				answer:
					"Some of our activities include free transfers, based on the terms and conditions for each activity. If a free transfer is not included, we can arrange a pickup service for an additional charge, depending on the pickup location and distance.\nIf you would like more information about pickup areas and transportation fees, please feel free to contact our customer service team via WhatsApp.",
			},
			{
				question: "Who will be our driver and what time will we be picked up?",
				answer:
					"Your driver’s name and contact number will be shared with you by the evening before your activity. The pick-up time will be arranged based on the session you selected at booking, the activities you have chosen, and the distance from your pickup location. This helps ensure that you arrive at Surya Bintang Adventures on time for your adventure. Our driver will contact you in advance to ensure your pickup details.",
			},
			{
				question:
					"If we book an activity include transport, will it be private or shared with other guests?",
				answer:
					"Our pickup service is provided as private transportation, so you will not be shared with other guests. Only you and your group in the same vehicle.\nThe type of vehicle is selected based on the number of guests, specific requirements (such as extra luggage space for large suitcases), and the accessibility of your accommodation location. Groups of 4–5 guests are typically assigned a large car, groups of 6–25 guests use a minibus, and groups of more than 25 guests are accommodated with a full-size bus.",
			},
			{
				question:
					"If I visit using my own transportation, what types of vehicles are able to access the Surya Bintang Adventures location?",
				answer:
					"Our location is easily accessible by both motorcycles and cars. Even a 45-seat bus can reach our site, thanks to the wide and well-maintained access roads.\nClear directions are available on Google Maps, and we also provide a direct location link for your convenience.\nIn addition, we offer a spacious parking area that can accommodate both private vehicles and large group transportation, including buses.",
			},
			{
				question:
					"Does SBA offer packages that include accommodation or villas?",
				answer:
					"Yes, we collaborate with trusted partners to provide complete travel solutions including accommodations, day tours, ticketing services, and other activities ensuring a seamless and comfortable experience for your entire journey.\nFor personalized offers and package details, feel free to contact our customer service team to arrange your needs.",
			},
			{
				question:
					"Does transportation include round trip hotel pickup and drop off?",
				answer:
					"Yes, hotel pickup and return are included in the transportation service unless stated otherwise",
			},
			{
				question:
					"How can I book transportation with Surya Bintang Adventures?",
				answer:
					"You can book your transportation directly through our website by selecting the vehicle type that best suits your needs.\nIf you are unsure which option to choose or need assistance regarding travel distance, luggage space, accessibility, or any other requirements, our customer service team will be happy to assist you.",
			},
		],
	},
	{
		name: "Food and Facilities",
		questions: [
			{
				question: "Is lunch included?",
				answer:
					"Yes. All our main activity packages include a complimentary, freshly prepared local style lunch, featuring a curated selection of signature dishes: Indonesian Fried Rice, Grilled Chicken, or Crispy Duck, each served with traditional accompaniments for an authentic Balinese dining experience.",
			},
			{
				question:
					"Does Surya Bintang Adventures provide vegetarian meal options?",
				answer:
					"Our standard vegetarian meal in the activity inclusion is fried rice.\nIf you would like more variety, we also offer additional vegetarian dishes, including Margherita Pizza, Vegetarian Pizza, French Fries, Onion Rings, Banana Fritters, and Spring Rolls. These items can be ordered separately in addition to the meal included in your activity package. To ensure availability, please indicate your dietary requirements clearly in the “Special Request” section when placing your booking, or notify our Customer Service team during your booking confirmation process.",
			},
			{
				question:
					"Does Surya Bintang Adventures offer kid-friendly meal options?",
				answer:
					"Currently, we do not have a specific menu designed for children. However, our chefs can prepare meals with lighter seasoning and milder flavors to suit younger guests. Please let our staff or customer service team know about any special requests, and we will be happy to assist whenever possible.",
			},
			{
				question: "What facilities are available on-site?",
				answer:
					"We’ve got everything to keep you comfortable: hot showers, lockers, clean toilets, towels, live music, a bar, restaurant with rice field views, hair dryers, charging outlets, and a small shop for souvenirs. Perfect for relaxing after your adventure!",
			},
			{
				question: "Is photo and video documentation available?",
				answer:
					"Yes, we offer optional documentation packages that include photos, videos, and drone footage at an additional cost. We capture your moments with professional-grade photography and videography using top-quality, high-end equipment. You may book this service either before or after your activity.",
			},
			{
				question:
					"Is there a waiting area for family members who are not joining the activity?",
				answer:
					"Yes, we provide a spacious and comfortable waiting area located at our onsite restaurant, surrounded by peaceful natural scenery. Guests can enjoy live music performances and sip on complimentary drinks like coffee or tea while waiting for their family or friends to finish the activity.\nWe also provide a dedicated prayer room (mushola) for Muslim guests who wish to perform their prayers during the visit.",
			},
		],
	},
	{
		name: "Time and Itenerary",
		questions: [
			{
				question: "What time is the activity available?",
				answer:
					"The timing depends on the activity you choose and the availability of time slots on that day. Each activity has its own detailed itinerary, including pick-up time, activity flow, and meal break. For the most accurate timing, we’ll provide you with a full schedule after booking, or you can check the activity’s page on our website for a complete breakdown.",
			},
			{
				question: "Is afternoon rafting available?",
				answer:
					"Unfortunately, we do not offer rafting trips after the designated check-in times, as afternoon weather and river conditions may affect the safety and overall quality of the experience.\nThe latest check-in time depends on the type of rafting trip you book:\n- Shared rafting: Last check-in at 10:30 AM (local time)\n- Private rafting: Last check-in at 1:30 PM (local time)\nFor safety reasons, we do not accept rafting activities beyond these times.",
			},
			{
				question: "What time is the last ATV and cycling ride?",
				answer:
					"The latest time for ATV rides is at 3:00 PM (local time), and for cycling activities, it is 2:00 PM (local time).\nIf you're planning to arrive later in the day, we recommend arriving before these cut-off times so you don't miss out on your chosen activity.",
			},
			{
				question: "If I book two activities, when should I arrive?",
				answer:
					"If you book two activities, please arrive by 10:00 AM at the latest. If you arrive after this time, you may choose to reschedule one of the activities to the next day, subject to our terms and conditions. For any schedule changes, please coordinate with our staff so we can note your request.",
			},
			{
				question: "Can the activity still be done during the rainy season?",
				answer:
					"Yes, our activities continue during the rainy season, as rafting is safe and even more thrilling with increased water flow. However, in the event of extreme weather or major flooding, trips may be postponed or canceled for safety reasons. Your safety is always our top priority.",
			},
			{
				question: "When is the best time to go rafting in Bali?",
				answer:
					"The best time for rafting in Bali is during the dry season, from April to October, when the weather is more stable and river conditions are ideal.\nHowever, rafting can still be a fantastic experience year round especially with a trusted operator like Surya Bintang Adventures, who ensures safety, comfort, and excitement in every season.",
			},
			{
				question: "What if I arrive late at the location?",
				answer:
					"We allow a maximum grace period of 30 minutes, depending on the situation on site. If you're experiencing delays, please contact our team immediately via WhatsApp as listed on our official platforms to assist you accordingly.",
			},
		],
	},
	{
		name: "Activity and Technical Details",
		questions: [
			{
				question: "What is the check-in process on the day of the activity?",
				answer:
					"Upon arrival at our meeting point, please follow these simple steps:\n1. Check in at the Reception Desk\n2. Complete the registration form, which includes your personal details and insurance information\n3. Enjoy a complimentary welcome drink and select your preferred lunch menu\n4. Our Front Office team will provide a briefing about the activity you chose and the inclusions of your package\n5. Complete any remaining payment for your activity, if applicable\n6. Receive your locker key and pay a refundable locker deposit of IDR 50,000. The deposit will be returned after the activity when the locker key is handed back\n7. Get ready for your adventure and board the Surya Bintang Adventures shuttle to the starting point",
			},
			{
				question:
					"Private rafting allows up to 3 people can we do 4 in one boat?",
				answer:
					"Yes, absolutely! Our rafting boats can accommodate up to 6 participants, so 4 people in one private boat is no problem. We usually recommend a maximum of 3 for private trips to enhance the exclusivity and comfort, but if your group prefers to go together as 4, we’re happy to arrange that for you.",
			},
			{
				question:
					"Can guests with injuries join the activity without paddling?",
				answer:
					"For safety reasons, we do not recommend rafting for guests with injuries, even if they are not paddling, as rafting requires good physical condition.",
			},
			{
				question: "What should I wear for rafting?",
				answer:
					"We recommend wearing comfortable, quick-drying clothes such as sportswear or swimwear, along with river sandals or outdoor shoes. Please also bring a change of clothes for after the activity.\nIf you don’t have the proper gear, don’t worry we have a merchandise corner onsite where you can purchase basic essentials like quick dry shirts, waterproof phone cases, and river sandals for your convenience.",
			},
			{
				question: "Can 2 adults and 1 child ride one ATV together?",
				answer:
					"For safety reasons, we allow a maximum of 2 people per ATV. Children under 6 years old may ride as a passenger with an adult (such as a parent) or with an additional ATV guide. Children will be provided with and required to wear a safety belt throughout the ATV activity.",
			},
			{
				question:
					"What is the difficulty level of the rafting and kayaking activities?",
				answer:
					"Our rafting and kayaking location is on the Ayung River, which is classified as Grade II–III. The river features moderately strong currents, light maneuvering requirements, and a few exciting rapids along the route. This difficulty level is suitable for beginners to intermediate participants, so it's great adventure for families, friends, and groups.",
			},
			{
				question: "Can I kayak independently or with a friend?",
				answer:
					"No. For safety reasons, all kayaking activities are conducted using tandem kayaks with one participant accompanied by one certified guide. Participants are not allowed to kayak independently or share a kayak with anyone other than our trained and certified guides. This policy helps ensure the highest standards of safety throughout the activity.",
			},
			{
				question:
					"Do I need to know how to swim to join the rafting or kayaking activities?",
				answer:
					"Not at all! You don't need to know how to swim to join our rafting or kayaking adventures. We provide all necessary safety gear, including a life jacket and helmet, and our professional guides will be with you every step of the way.\nBefore the trip begins, you'll receive a full safety briefing and easy-to-follow paddling instructions. If you have any fears, concerns, or special requests, simply let your guide know so we can make sure you have a safe, comfortable, and enjoyable experience.",
			},
		],
	},
	{
		name: "Safety & Participant Requirements",
		questions: [
			{
				question: "What are the participant requirements for rafting and ATV?",
				answer:
					"Participants must meet the following requirements:\n- Minimum height: 120 cm\n- Maximum weight: 120 kg\n- Be in good physical condition (no serious heart problems, back injuries, or pregnancy)\n- Children must be accompanied by an adult",
			},
			{
				question: "Is rafting suitable for beginners and children?",
				answer:
					"Yes, rafting with Surya Bintang Adventures is safe for beginners and children aged 5 and above. We provide complete safety equipment and certified professional guides to ensure a fun and secure experience.",
			},
			{
				question: "What safety standards are implemented during the activity?",
				answer:
					"Surya Bintang Adventures provides international standard safety equipment, including helmets and life jackets, for all participants. Before starting, guests will receive a detailed safety briefing. All equipment is regularly inspected, and our certified guides are trained in safety protocols, Prosthesis, CPR, and first aid to ensure the highest level of safety during your adventure.",
			},
			{
				question:
					"Are there any health restrictions for joining rafting or ATV activities?",
				answer:
					"Yes. These activities are not recommended for individuals with epilepsy, heart conditions, pregnant women, or those who are not in good physical health.",
			},
			{
				question: "Does SBA provide insurance coverage for participants?",
				answer:
					"Yes, all participants aged 5 to 65 years old are automatically covered by insurance during the activity.\nFor detailed information about the coverage and terms, please visit our About Us page.",
			},
			{
				question:
					"What is the procedure if an accident occurs during the activity?",
				answer:
					"Surya Bintang Adventures has a clear and well trained emergency response protocol (SOP) in place. In case of any accident, our team will immediately provide first aid on-site, coordinate with medical services, and activate the participant’s insurance coverage for further assistance. Your safety is our top priority.",
			},
			{
				question: "Does SBA provide professional guides for every activity?",
				answer:
					"Yes, all our activities are led by certified guides officially licensed by the Bali Tourism Office, Gahawisri, and FAJI. They are also trained in CPR and first-aid response. With years of experience, our guides are dedicated to ensuring both your safety and an enjoyable adventure.\nOur team not only prioritizes your safety, but also strives to create unforgettable experiences through their expertise, warm hospitality, and knowledge of Bali’s nature and culture.",
			},
		],
	},
	{
		name: "Custom Requests & Special Needs",
		questions: [
			{
				question: "Can we celebrate a birthday there?",
				answer:
					"Yes! We can help organize a small celebration with decorations, cake, and documentation. Let us know your ideas.",
			},
			{
				question: "Do you have group or company outing packages?",
				answer:
					"Absolutely! Our venue can accommodate up to 600 guests per day and welcomes both small and large group bookings, including corporate teams, private groups, and organizations. For groups of more than 100 participants, please reach out to our reservation team in advance via WhatsApp to secure space on your activity date.",
			},
			{
				question: "Are guests allowed to bring outside food?",
				answer:
					"Guests are not allowed to bring outside food. However, if you wish to bring food from outside, a corkage fee will apply, subject to terms and conditions.",
			},
			{
				question: "Do you offer combo packages with transfers?",
				answer:
					"Yes, we offer exciting combo packages, such as Rafting + ATV or Cycling + Rafting. Most combos also include transfers for added convenience.",
			},
			{
				question: "Does Surya Bintang Adventures offer a buffet service?",
				answer:
					"Yes. We can arrange a buffet-style catering service for groups with a minimum of 30 participants. Please specify this request at the time of booking or during your booking confirmation so our team can prepare and coordinate accordingly.",
			},
			{
				question:
					"Can Surya Bintang Adventures arrange special activities such as team building and fun games?",
				answer:
					"Yes. We have extensive experience in organizing fun games and team building programs for various companies and organizations. You may view references and examples of these activities in the Activities section on our website. For bookings, please include this request when placing your reservation or during the confirmation process with our Customer Service team.",
			},
		],
	},
]);

// States for category dropdowns
const categoryStates = ref<boolean[]>([
	false,
	false,
	false,
	false,
	false,
	false,
	false,
]);

// States for question dropdowns within each category
const questionStates = ref<boolean[][]>([
	[false, false],
	[false, false],
	[false, false],
	[false, false],
	[false, false],
	[false, false],
	[false, false],
]);

const toggleCategory = (categoryIndex: number) => {
	categoryStates.value[categoryIndex] = !categoryStates.value[categoryIndex];
};

const toggleQuestion = (categoryIndex: number, questionIndex: number) => {
	questionStates.value[categoryIndex][questionIndex] =
		!questionStates.value[categoryIndex][questionIndex];
};

// Desktop FAQ functionality
const selectedCategory = ref(0); // Default to first category
const desktopQuestionStates = ref<boolean[]>([false, false]); // States for desktop questions

const selectCategory = (categoryIndex: number) => {
	selectedCategory.value = categoryIndex;
	// Reset question states when switching categories
	desktopQuestionStates.value = Array(
		getSelectedCategoryQuestions().length,
	).fill(false);
};

const getSelectedCategoryQuestions = () => {
	return faqCategories.value[selectedCategory.value]?.questions || [];
};

const toggleDesktopQuestion = (questionIndex: number) => {
	desktopQuestionStates.value[questionIndex] =
		!desktopQuestionStates.value[questionIndex];
};

/** Full FAQ payload for FAQPage JSON-LD (explicit Q/A for crawlers; avoids relying on accordion visibility). */
const faqPageJsonLd = computed(() => ({
	"@context": "https://schema.org",
	"@type": "FAQPage",
	mainEntity: faqCategories.value.flatMap((category) =>
		category.questions.map((item) => ({
			"@type": "Question",
			name: item.question,
			acceptedAnswer: {
				"@type": "Answer",
				text: item.answer,
			},
		})),
	),
}));

useHead({
	script: [
		{
			type: "application/ld+json",
			tagPriority: "low",
			children: JSON.stringify(faqPageJsonLd.value),
		},
	],
});
</script>
